import { StorageEvents } from "../../../core/router/auth/SecurityManager";
import type { QueryParamsConfig } from "./serverApi";

const STORAGE_PREFIX = "table-state:";

/**
 * Table filter/sort/pagination state is kept in sessionStorage (not localStorage) so it
 * survives navigating to a detail page and back, but doesn't leak stale filters into a new tab/session.
 * Keyed by the page's URL path rather than a component prop, since it needs to be readable both from
 * ResourceManager (which owns the query) and from SearchForm/GenericTable (which render restored state),
 * without threading a new prop through the many per-feature wrapper components in between.
 */
export function loadPersistedQueryConfig<T>(pageKey: string): QueryParamsConfig<T> | undefined {
    try {
        const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${pageKey}`);
        return raw ? (JSON.parse(raw) as QueryParamsConfig<T>) : undefined;
    } catch {
        return undefined;
    }
}

export function persistQueryConfig<T>(pageKey: string, queryConfig: QueryParamsConfig<T>): void {
    try {
        sessionStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, JSON.stringify(queryConfig));
    } catch {
        // best-effort only, e.g. sessionStorage disabled or full
    }
}

/**
 * Search criteria (and, going forward, which criteria even apply) can differ per data group, so a
 * filter persisted under one data group can reference an entity that doesn't exist in another, or a
 * field that isn't shown at all — silently making an unrelated data group's table look empty. Wipe
 * everything on data-group change rather than trying to guess which persisted filters are still valid.
 */
export function clearAllPersistedQueryConfigs(): void {
    try {
        Object.keys(sessionStorage)
            .filter((key) => key.startsWith(STORAGE_PREFIX))
            .forEach((key) => sessionStorage.removeItem(key));
    } catch {
        // best-effort only, e.g. sessionStorage disabled
    }
}

if (typeof window !== "undefined") {
    window.addEventListener(StorageEvents.DATA_GROUP_CHANGED, clearAllPersistedQueryConfigs);
}
